/**
 * Large print exports (e.g. 30×30in at 300 DPI = 9000²) stay as Blob object
 * URLs instead of data: URLs. Safari cannot decode or fetch() data: URLs of
 * that size, which dropped the garment preview and blocked Create.
 */

// Safari handled ~20MB data: URLs in testing and failed at ~130MB.
export const INLINE_PRINT_MAX_BYTES = 16 * 1024 * 1024;

const MAX_HELD = 32;
const held = new Map();

export const isBlobUrl = (value) =>
  typeof value === "string" && value.startsWith("blob:");

/** Keep the Blob reachable by its object URL so save can stage it synchronously. */
export function holdPrintBlob(blob) {
  const url = URL.createObjectURL(blob);
  held.set(url, blob);
  while (held.size > MAX_HELD) {
    const [oldest] = held.keys();
    held.delete(oldest);
    URL.revokeObjectURL(oldest);
  }
  return url;
}

export const getHeldPrintBlob = (value) =>
  isBlobUrl(value) ? held.get(value) || null : null;

const fileSources = new WeakMap();

/** Files made from the same held print share a source, so staging can upload it once. */
export function fileFromHeldPrint(value, filename) {
  const blob = getHeldPrintBlob(value);
  if (!blob) return null;
  const file = new File([blob], filename, { type: blob.type || "image/png" });
  fileSources.set(file, blob);
  return file;
}

export const heldPrintSourceOf = (file) =>
  (file && typeof file === "object" && fileSources.get(file)) || null;

/** A print/preview source the save path can turn into an upload. */
export const isUsableImageSrc = (value) =>
  typeof value === "string" &&
  ((value.startsWith("data:") && value !== "data:," && value.length >= 100) ||
    Boolean(getHeldPrintBlob(value)));

export const canvasToBlob = (canvasEl, type = "image/png", quality) =>
  new Promise((resolve) => {
    try {
      canvasEl.toBlob((blob) => resolve(blob || null), type, quality);
    } catch (_) {
      resolve(null);
    }
  });

export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** Small files keep today's data: URL; large ones become a held object URL. */
export const blobToImageSrc = async (blob) =>
  blob.size > INLINE_PRINT_MAX_BYTES ? holdPrintBlob(blob) : blobToDataUrl(blob);

/** Run at most `max` tasks at once; each color's mockup holds several large canvases. */
export function createLimiter(max) {
  let active = 0;
  const queue = [];
  const next = () => {
    if (active >= max || !queue.length) return;
    active += 1;
    const { task, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(task)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        next();
      });
  };
  return (task) =>
    new Promise((resolve, reject) => {
      queue.push({ task, resolve, reject });
      next();
    });
}

/**
 * Downscale with repeated halving so large photos/art don't alias when drawn
 * at mockup size. Returns the source unchanged when it is already small enough.
 */
export function downscaleForDraw(source, targetWidth, targetHeight) {
  const srcW = source.naturalWidth || source.width || 0;
  const srcH = source.naturalHeight || source.height || 0;
  const tw = Math.max(1, Math.round(targetWidth));
  const th = Math.max(1, Math.round(targetHeight));
  if (!srcW || !srcH || (srcW <= tw * 1.05 && srcH <= th * 1.05)) {
    return source;
  }
  let current = source;
  let w = srcW;
  let h = srcH;
  const step = (next, nw, nh) => {
    const out = drawScaled(next, nw, nh);
    if (next !== source) {
      next.width = 0;
      next.height = 0;
    }
    return out;
  };
  while (w / 2 >= tw && h / 2 >= th) {
    w = Math.round(w / 2);
    h = Math.round(h / 2);
    current = step(current, w, h);
    if (!current) return source;
  }
  return step(current, tw, th) || source;
}

function drawScaled(source, w, h) {
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, w, h);
  return out;
}
